/** @file mergeSource: copies a source puppet's nodes, drawables, sheets, clips and images into the host. */
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { CommandContext, PuppetSource } from '../context/commandContext';
import { renameClipNodes } from '../library/renameClipNodes';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';

/** A source drawable as it enters the host: id and sheets prefixed, nodes renamed. */
function hostDrawable(d: Drawable, prefix: string, to: (n: string) => string): Drawable {
  const out = structuredClone(d);
  out.id = prefix + d.id;
  out.node = to(d.node);
  if (out.kind === 'impostor' && out.anchor?.node !== undefined) out.anchor.node = to(out.anchor.node);
  if (out.kind === 'effect') out.frames.sheet = prefix + out.frames.sheet;
  const facets = out.kind === 'impostor' || out.kind === 'effect' ? (out.facets ?? []) : [];
  for (const e of facets) e.sheet = prefix + e.sheet;
  return out;
}

/**
 * Adds the source's non-merged nodes (parents and twins renamed through `rename`), its drawables and sheets under
 * `prefix`, its clips as `prefix + name` with new ids and renamed nodes, and its images; returns the clip names.
 */
export function mergeSource(draft: PuppetDoc, src: PuppetSource, rename: ReadonlyMap<string, string>, prefix: string, ctx: Pick<CommandContext, 'makeId'>): string[] {
  const p = draft.puppet;
  const to = (n: string): string => rename.get(n) ?? n;
  for (const [name, node] of Object.entries(src.puppet.nodes)) {
    const host = to(name);
    if (host !== prefix + name) continue;
    const copy = structuredClone(node);
    if (copy.parent !== undefined) copy.parent = to(copy.parent);
    if (copy.mirror !== undefined) copy.mirror = to(copy.mirror);
    p.nodes[host] = copy;
  }
  p.drawables.push(...src.puppet.drawables.map((d) => hostDrawable(d, prefix, to)));
  for (const [name, sheet] of Object.entries(src.puppet.sheets)) p.sheets[prefix + name] = structuredClone(sheet);
  const map = Object.fromEntries(rename);
  const clips = Object.entries(src.clips).map(([name, c]) => {
    draft.clips[prefix + name] = { ...renameClipNodes(c, map), id: ctx.makeId('clp'), name: prefix + name };
    return prefix + name;
  });
  for (const [id, meta] of Object.entries(src.images)) draft.images[id] ??= structuredClone(meta);
  return clips;
}
